import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { Context } from "../App";
import AdminLayout from "../components/AdminLayout";
import AdminDateInput from "../components/AdminDateInput";
import * as Constants from "../api/constants";
import "../adminConsoleTheme.css";

const TRACKING_FROM = "2026-05-01";

const FAILED_STATUSES = new Set([
  "ONLINE PAYMENT FAILED",
  "PAYMENT ABANDONED",
  "LAPSE",
  "SAVE NOW PAY LATER",
]);

function defaultToDate() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().split("T")[0];
}

export default function AdminPaymentFailed() {
  const gridRef = useRef();
  const navigate = useNavigate();
  const { context } = useContext(Context);
  const [fromDate, setFromDate] = useState(TRACKING_FROM);
  const [toDate, setToDate] = useState(defaultToDate);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!context.isAdminLoggedIn) {
      navigate("/admin", { replace: true });
    }
  }, [context.isAdminLoggedIn, navigate]);

  const loadRows = useCallback(async () => {
    if (!fromDate || !toDate) {
      setError("Choose both From and To dates.");
      return;
    }
    if (fromDate > toDate) {
      setError("From date must be on or before To date.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(Constants.FIND_FAILED_ONLINE_PAYMENTS, {
        date: fromDate,
        toDate,
        limit: 500,
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setRows(
        list.filter(
          (r) =>
            String(r.paymentMode || "").toUpperCase() === "ONLINE" &&
            FAILED_STATUSES.has(String(r.paymentStatus || "").toUpperCase())
        )
      );
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Could not load failed payments.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    if (context.isAdminLoggedIn) {
      loadRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, [context.isAdminLoggedIn]);

  const columnDefs = useMemo(
    () => [
      { headerName: "Customer", field: "customerName", width: 140 },
      { headerName: "Phone", field: "phoneNumber", width: 120 },
      { headerName: "Event date", field: "eventDate", width: 110 },
      { headerName: "Theatre", field: "theatreName", width: 140 },
      { headerName: "Slot", field: "slotName", width: 120 },
      { headerName: "Payment status", field: "paymentStatus", width: 170 },
      { headerName: "Booking ref", field: "bookingRefId", width: 180 },
      { headerName: "Total", field: "totalAmount", width: 90 },
    ],
    []
  );

  return (
    <AdminLayout title="Partyboss Payment Failed">
      <div className="admin-console-content p-3">
        <div className="admin-console-card p-3 mb-3">
          <h1 className="h5 text-info mb-2">Partyboss Payment Failed</h1>
          <p className="text-secondary small mb-3">
            Online bookings where advance payment was not completed (failed, abandoned, timed out, or still pending).
            Only bookings created on or after 1 May 2026 are tracked. These do not block theatre slots.
          </p>
          <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
            <div>
              <Form.Label className="text-white-50 small d-block mb-1">From (event date)</Form.Label>
              <AdminDateInput
                size="sm"
                style={{ width: 180 }}
                min={TRACKING_FROM}
                max={toDate || undefined}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <Form.Label className="text-white-50 small d-block mb-1">To (event date)</Form.Label>
              <AdminDateInput
                size="sm"
                style={{ width: 180 }}
                min={fromDate || TRACKING_FROM}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <Button variant="outline-info" size="sm" onClick={loadRows} disabled={loading}>
              {loading ? "Loading…" : "Search"}
            </Button>
          </div>
          {error ? <Alert variant="danger">{error}</Alert> : null}
          {loading && rows.length === 0 ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-secondary small mb-0">No failed or pending online payments in this date range.</p>
          ) : (
            <div className="admin-console-grid-wrap admin-console-grid-wrap--light" style={{ height: 520 }}>
              <AgGridReact
                ref={gridRef}
                rowData={rows}
                columnDefs={columnDefs}
                defaultColDef={{ sortable: true, filter: true, resizable: true }}
                pagination
                paginationPageSize={25}
              />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
